/**
 * Pure helpers behind the lane-v2 task views — grouping, labelling, counting, diffing.
 *
 * NO REACT HERE. Every function takes plain data (as shaped by
 * `services/multi-agent/laneV2/types.ts`) and returns plain data or strings, so it is unit-testable
 * without rendering anything and reusable from any of the view components (and, per the task brief,
 * from the other implementer's QA harness).
 *
 * OPEN ENUMS, EVERYWHERE. `types.ts` is explicit that every enum-shaped field is really `KnownValue |
 * string`, because the server can ship a new value before this client knows its label. Every labeller
 * below therefore has a closed map for the values it knows and a verbatim fallback for anything else —
 * never a thrown error, never a silently dropped row.
 */
import type {
  BuildStatus,
  BuildView,
  CheckView,
  CoverageRecord,
  ExcludedComponentView,
  OrgFindingView,
  PackageMemberView,
  PlanItemView,
  SaveBranchFileResult,
  TaskState,
  TransitionView,
  WorkItemStatus,
  WorkItemView,
} from "@/services/multi-agent/laneV2/types";

// ── short ids / hashes ──────────────────────────────────────────────────────────────────────────

/** First `len` chars of an id/hash, or the id itself if it is already shorter. `null`/`undefined` → "". */
export function shortId(id: string | null | undefined, len = 8): string {
  if (!id) return "";
  return id.length <= len ? id : id.slice(0, len);
}

/** Same idea, default length 12 (a sha256 prefix long enough to be practically unique for a human). */
export function shortSha(sha: string | null | undefined, len = 12): string {
  return shortId(sha, len);
}

// ── the journey (TaskStateStepper) ──────────────────────────────────────────────────────────────

export interface JourneyStep {
  key: TaskState;
  label: string;
}

/** The main line of the journey, in order. Side states and terminal states are NOT in this list —
 *  they are overlays on it (see `stepperAnchor` / `SIDE_STATE_LABEL` / `TERMINAL_STATE_LABEL`). */
export const JOURNEY_STEPS: readonly JourneyStep[] = [
  { key: "exploring", label: "Understanding" },
  { key: "plan_proposed", label: "Plan" },
  { key: "contracting", label: "Preparing" },
  { key: "building", label: "Building" },
  { key: "assembling", label: "Packaging" },
  { key: "changeset_proposed", label: "Review" },
  { key: "checking", label: "Checking" },
  { key: "validated", label: "Ready" },
  { key: "applying", label: "Apply" },
  { key: "applied", label: "Applied" },
] as const;

/** A state that means "the agent is waiting on a person", rendered as an overlay on its nearest
 *  journey step rather than as a step of its own — there is no fixed place for it in the line. */
export const SIDE_STATE_LABEL: Record<string, string> = {
  needs_input: "waiting for you",
  needs_budget: "waiting for you — more budget needed",
  amendment_proposed: "waiting for you — plan change proposed",
};

/** A state the task does not leave. Shown distinctly from the journey line, never as "done". */
export const TERMINAL_STATE_LABEL: Record<string, string> = {
  applied: "Applied",
  cancelled: "Cancelled",
  failed: "Failed",
};

/**
 * The journey line to draw for this task. A forward task has no deploy step (contracts 0.2.30): it ends at "Ready" and
 * rests there, so the "Applying" / "Applied" steps are drawn only for a task that is, or went, through applying (a revert).
 */
export function journeyStepsFor(state: string, transitions: readonly TransitionView[]): readonly JourneyStep[] {
  const applying = state === "applying" || state === "applied" || transitions.some((t) => t.to === "applying" || t.to === "applied");
  return applying ? JOURNEY_STEPS : JOURNEY_STEPS.filter((s) => s.key !== "applying" && s.key !== "applied");
}

export function isSideState(state: string): boolean {
  return state in SIDE_STATE_LABEL;
}

export function isTerminalState(state: string): boolean {
  return state in TERMINAL_STATE_LABEL;
}

/**
 * Which journey step a side state or a terminal state (or the current state, if it is itself a plain
 * journey step) should be shown against. For a side state this is the step the task was doing when it
 * started waiting; for a terminal state it is the step the task had reached when it stopped — a
 * `cancelled` task that got as far as `changeset_proposed` should still show that much of the line as
 * done, not a blank line. Walks `transitions` backwards for the most recent transition landing on
 * `state` and anchors on that transition's `from`. Falls back to the state itself (rendered verbatim
 * if unknown) when there is nothing to anchor on, which only happens on a snapshot with no transition
 * history yet.
 */
export function stepperAnchor(state: string, transitions: readonly TransitionView[]): string {
  // `applied` is both a terminal state AND the journey's own last step — it anchors on itself, not
  // on whatever preceded it.
  if (JOURNEY_STEPS.some((s) => s.key === state)) return state;
  if (!isSideState(state) && !isTerminalState(state)) return state;
  for (let i = transitions.length - 1; i >= 0; i--) {
    const t = transitions[i];
    if (t.to === state && t.from) return t.from;
  }
  return state;
}

// ── plan items ───────────────────────────────────────────────────────────────────────────────────

const OP_ORDER = ["create", "modify", "delete"] as const;

export interface OpGroup<T> {
  op: string;
  items: T[];
}

/** Group items carrying an `op` field into create/modify/delete (in that order), then any other op
 *  the server sends, in first-seen order — an unknown op is still a group, never dropped. */
export function groupByOp<T extends { op: string }>(items: readonly T[]): OpGroup<T>[] {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const list = buckets.get(item.op);
    if (list) list.push(item);
    else buckets.set(item.op, [item]);
  }
  const ordered: OpGroup<T>[] = [];
  for (const op of OP_ORDER) {
    const items2 = buckets.get(op);
    if (items2) {
      ordered.push({ op, items: items2 });
      buckets.delete(op);
    }
  }
  for (const [op, items2] of buckets) ordered.push({ op, items: items2 });
  return ordered;
}

export const OP_LABEL: Record<string, string> = {
  create: "Create",
  modify: "Modify",
  delete: "Delete",
};

export function opLabel(op: string): string {
  return OP_LABEL[op] ?? op;
}

/** How many plan items name each `depends_on` id, so a reader can see a dependency's fan-out without
 *  walking the whole list themselves. Not currently required by any view, kept here because
 *  `PlanRevisionView`'s dependency rendering and any future "who depends on this" view share it. */
export function dependentCounts(items: readonly PlanItemView[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    for (const dep of item.depends_on ?? []) {
      counts.set(dep, (counts.get(dep) ?? 0) + 1);
    }
  }
  return counts;
}

// ── out-of-scope ─────────────────────────────────────────────────────────────────────────────────

/** `OutOfScopeView.reason` is open (`app/core` may add a new one). Known reasons get plain-English
 *  copy; anything else renders the server's own string rather than a generic "unknown reason". */
export const OUT_OF_SCOPE_REASON_LABEL: Record<string, string> = {
  unsupported_type: "not something automation can build yet",
  modify_of_create_only_type: "this type can only be created, not changed, by automation",
  manual_prerequisite: "needs something set up in the org first",
  needs_human: "needs a person to do this part",
};

export function outOfScopeReasonLabel(reason: string): string {
  return OUT_OF_SCOPE_REASON_LABEL[reason] ?? reason;
}

// ── package member validation ────────────────────────────────────────────────────────────────────

/** Badge copy per `validation_status` (open enum: an unknown value renders as itself). */
export const VALIDATION_STATUS_LABEL: Record<string, string> = {
  validated: "Checked",
  not_validated: "Not checked",
  validation_failed: "Check failed",
};

/** Plain words for a member held back from the changeset (`validation_fact.reason === "held_back"`; MSA never sends a `held` status). */
export const HELD_BACK_LABEL = "Held back";

/** True when the member's validation fact says it was held back. Reads the typed reason code, never prose. */
export function isHeldBack(member: { validation_fact?: { reason?: string | null } | null }): boolean {
  return member.validation_fact?.reason === "held_back";
}

export function validationStatusLabel(status: string): string {
  return VALIDATION_STATUS_LABEL[status] ?? status;
}

/**
 * TEMPORARY client map, reason code → sentence, until the server sends the sentence itself (wave 1
 * contracts release). Most `validation_reasons` the server writes are already sentences; these are
 * the machine codes (MSA `work/builds.py` `NO_GENERATED_TEST_REASON`, `_SUBJECT_NOT_VALIDATED_REASON`).
 * An unknown code renders as its humanised words (`validationReasonLabel`); the raw code stays on the row title.
 */
export const VALIDATION_REASON_LABEL: Record<string, string> = {
  no_generated_test_by_policy: "No test was generated for this change, so nothing exercises it yet.",
  covering_test_blocked: "The test that covers this change could not be built, so it is not exercised.",
  "level_check_waiver: carried_pending_recheck": "A check was waived for now and will be re-run later.",
  subject_not_validated: "The component this test covers is not validated, so the test was not run.",
  tests_not_run: "Tests have not been run against this yet.",
  no_body_written: "Not changed: the build did not write this component.",
  org_check_accepted: "The org accepted this in a check-only deploy.",
  test_not_relevant: "This added test does not call anything that changed in the class it covers, so it is kept but not counted as a test for the change.",
  assumed_value: "The build used a value you did not give; check it before saving.",
  baseline_merge_doc_refused: "The change was written, but it could not be lined up with the org's current copy of this file to record a safe 'before' version (needed for revert and the overwrite check), so it is held out of the changeset.",
  // MSA's resting reason for a built member no org check has run on yet (dev 2026-11-02, work_items.validation_reasons).
  // Typed "the build did not write this" codes (MSA core/member_reasons.py); the server sentence wins when it sends one.
  customer_test_not_edited: "Existing test: it is run against the change, never edited.",
  required_site_not_changed: "A required change was not made: the build did not change this component.",
  unchecked: "Not checked against the org yet. Use Validate against org to check it.",
};

/** The reason the build left a member's body as the org's own: nothing was written for it (dev 2026-10-31). */
export const NOT_WRITTEN_REASON = "no_body_written";

/** An existing customer test class: run against the change, never edited. Not a failure and not missed work. */
export const CUSTOMER_TEST_REASON = "customer_test_not_edited";

/** A required change the build did not make: missed work, never hidden. */
export const REQUIRED_SITE_REASON = "required_site_not_changed";

/** Chip copy for that member: it is listed in the changeset but did not change. */
export const NOT_WRITTEN_LABEL = "Not changed";

const MACHINE_CODE = /^[a-z][a-z0-9_]*$/;

/** The leading machine code of a reason, if it has one ("no_body_written: not attempted - ..." → "no_body_written"). */
export function reasonCode(reason: string): string | null {
  const head = reason.split(":", 1)[0]!.trim();
  return MACHINE_CODE.test(head) ? head : null;
}

/** A machine token as words: underscores to spaces, first letter capitalised ("budget_out" → "Budget out"). */
function humaniseCode(code: string): string {
  const words = code.replace(/_+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const REASON_PARAM = /^[A-Za-z0-9_.-]+$/;
const PLAIN_PARAM = /^[a-z0-9_]+$/;

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The label plus whether it is one of this screen's own mapped sentences (`mapped`), see `validationReasonLabel`. */
function resolveReason(reason: string): { label: string; mapped: boolean } {
  const exact = VALIDATION_REASON_LABEL[reason];
  if (exact) return { label: exact, mapped: true };
  const text = reason.trim();
  if (!text) return { label: "", mapped: false };
  const code = reasonCode(text);
  if (code === null) return { label: reason, mapped: false };
  const known = VALIDATION_REASON_LABEL[code];
  const base = known ?? humaniseCode(code);
  const afterColon = text.slice(code.length).replace(/^\s*:/, "");
  const rest = afterColon.trim();
  if (!rest) return { label: base, mapped: known !== undefined };
  if (/^\s/.test(afterColon)) {
    // "code: sentence": the server owns the words. A single bare token after the colon is a param, not a sentence.
    if (known && !/\s/.test(rest)) return { label: known, mapped: true };
    return { label: capitalise(rest), mapped: false };
  }
  // "code:param": only a plain token counts (so "https://host/x" and the like pass through unchanged).
  if (!REASON_PARAM.test(rest)) return { label: reason, mapped: false };
  const param = PLAIN_PARAM.test(rest) ? rest.replace(/_+/g, " ") : rest;
  return { label: `${base} (${param})`, mapped: known !== undefined };
}

/**
 * A reason as words:
 *  - "code: sentence" (colon + space) shows the server's sentence, capitalised, even for a mapped code; the one exception
 *    is a lone token after the colon ("level_check_waiver: carried_pending_recheck"), which is a param and keeps the map;
 *  - "code:param" (no space) shows the mapped label, else the humanised code, then the param in brackets
 *    (a plain [a-z0-9_] param is humanised, anything else such as "Account.Field__c" is verbatim);
 *  - a bare code shows the mapped label, else the humanised code (never a generic line);
 *  - anything else (a sentence, a URL) passes through unchanged. The raw reason stays on the row's `title`.
 */
export function validationReasonLabel(reason: string): string {
  return resolveReason(reason).label;
}

/** True when the label shown is one of this screen's own mapped sentences (so the raw code need not be on the row's title). */
export function hasReasonLabel(reason: string): boolean {
  return resolveReason(reason).mapped;
}

const OP_WORD: Record<string, string> = { create: "Create", modify: "Modify", delete: "Delete" };
const OP_BLURB: Record<string, string> = {
  create: "Adds a new component to the org.",
  modify: "Changes a component that already exists in the org.",
  delete: "Removes a component from the org.",
};

/** The op as a word, for the glyph's tooltip and its accessible name. */
export function memberOpWord(op: string, notWritten: boolean): string {
  return notWritten ? NOT_WRITTEN_LABEL : (OP_WORD[op] ?? op);
}

/** One line saying what that op means for the org. */
export function memberOpBlurb(op: string, notWritten: boolean): string {
  return notWritten ? "Listed in the changeset, but the build did not write it." : (OP_BLURB[op] ?? "");
}

/** Reason codes that only restate the status word (every validated row carries one): never shown as a note. */
export function isInformationalReason(reason: string): boolean {
  return reasonCode(reason) === "org_check_accepted";
}

export type NotWrittenKind = "required_site" | "customer_test" | "no_body";

const NOT_WRITTEN_KIND_BY_CODE: Record<string, NotWrittenKind> = {
  [REQUIRED_SITE_REASON]: "required_site",
  [CUSTOMER_TEST_REASON]: "customer_test",
  [NOT_WRITTEN_REASON]: "no_body",
};

/** Why the build wrote nothing for a member, by typed reason code (never prose); missed work wins over the rest. The wire has
 *  no typed fact for it (`validation_fact.reason` has no not-written code), so the reason codes are the source. */
export function notWrittenKind(member: { validation_reasons?: string[] | null }): NotWrittenKind | null {
  const kinds = (member.validation_reasons ?? []).flatMap((r) => {
    const code = reasonCode(r);
    const kind = code ? NOT_WRITTEN_KIND_BY_CODE[code] : undefined;
    return kind ? [kind] : [];
  });
  return (["required_site", "customer_test", "no_body"] as const).find((k) => kinds.includes(k)) ?? null;
}

/** True when the build wrote nothing for this member, so it did not change. */
export function isNotWritten(member: { validation_reasons?: string[] | null }): boolean {
  return notWrittenKind(member) !== null;
}

/** The status word and tone of a not-written member: "Run only" (muted), "Not done" (warn), else "Not changed" (warn). */
export function notWrittenStatus(kind: NotWrittenKind | null): { label: string; tone: "muted" | "warn" } {
  if (kind === "customer_test") return { label: "Run only", tone: "muted" };
  if (kind === "required_site") return { label: "Not done", tone: "warn" };
  return { label: NOT_WRITTEN_LABEL, tone: "warn" };
}

/** Changed members first, then the not-changed ones, each with its index in the original list; `notDone` counts missed work. */
export function partitionMembers<T extends { validation_reasons?: string[] | null; validation_status?: string | null; validation_fact?: { reason?: string | null } | null }>(
  members: readonly T[],
): { changed: { m: T; i: number }[]; notChanged: { m: T; i: number }[]; notDone: number } {
  const changed: { m: T; i: number }[] = [];
  const notChanged: { m: T; i: number }[] = [];
  // A failed or held-back member is news: it stays in the visible list even when it carries a not-written code.
  members.forEach((m, i) => (isNotWritten(m) && m.validation_status !== "validation_failed" && !isHeldBack(m) ? notChanged : changed).push({ m, i }));
  const notDone = notChanged.filter(({ m }) => notWrittenKind(m) === "required_site").length;
  return { changed, notChanged, notDone };
}

// ── coverage ─────────────────────────────────────────────────────────────────────────────────────

export interface CoverageVerdictCount {
  verdict: string;
  count: number;
}

/** Counts of coverage units by verdict, in first-seen order — `verdict` is open (`planned` /
 *  `out_of_scope` / `missing` are the known ones; anything else still gets counted and shown). */
export function coverageVerdictCounts(units: CoverageRecord["units"]): CoverageVerdictCount[] {
  const counts = new Map<string, number>();
  for (const u of units ?? []) {
    counts.set(u.verdict, (counts.get(u.verdict) ?? 0) + 1);
  }
  return Array.from(counts, ([verdict, count]) => ({ verdict, count }));
}

export const COVERAGE_VERDICT_LABEL: Record<string, string> = {
  planned: "planned",
  out_of_scope: "out of scope",
  missing: "missing from the plan",
  // Contracts 0.2.17 `CoverageUnit.verdict` spells these two differently from the earlier draft
  // the first two rows came from; both spellings label the same thing.
  disclosed_out_of_scope: "out of scope",
  not_addressed: "missing from the plan",
};

export function coverageVerdictLabel(verdict: string): string {
  return COVERAGE_VERDICT_LABEL[verdict] ?? verdict;
}

// ── build / work items ──────────────────────────────────────────────────────────────────────────

/** Status groups in the order a reader should see them — the statuses that need attention first,
 *  then the statuses that are just progress, then anything the server invents later. */
const STATUS_ORDER: readonly WorkItemStatus[] = [
  "failed",
  "blocked",
  "dropped",
  "leased",
  "pending",
  "built",
  "validated",
];

export function groupByStatus<T extends { status: string }>(items: readonly T[]): OpGroup<T>[] {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const list = buckets.get(item.status);
    if (list) list.push(item);
    else buckets.set(item.status, [item]);
  }
  const ordered: OpGroup<T>[] = [];
  for (const status of STATUS_ORDER) {
    const items2 = buckets.get(status);
    if (items2) {
      ordered.push({ op: status, items: items2 });
      buckets.delete(status);
    }
  }
  for (const [status, items2] of buckets) ordered.push({ op: status, items: items2 });
  return ordered;
}

export const WORK_ITEM_STATUS_LABEL: Record<string, string> = {
  pending: "pending",
  leased: "in progress",
  built: "built",
  validated: "validated",
  failed: "failed",
  blocked: "blocked",
  dropped: "dropped",
};

export function workItemStatusLabel(status: string): string {
  return WORK_ITEM_STATUS_LABEL[status] ?? status;
}

/** Counts by status — prefers the server's own `BuildView.counts` (rule 21: never re-derive a number
 *  the backend already computed), falling back to counting `items` itself when `counts` is absent
 *  (an older snapshot shape, or a build with zero items). */
export function buildCounts(build: Pick<BuildView, "counts" | "items">): Record<string, number> {
  if (build.counts && Object.keys(build.counts).length > 0) return build.counts;
  const counts: Record<string, number> = {};
  for (const item of build.items) counts[item.status] = (counts[item.status] ?? 0) + 1;
  return counts;
}

export const BUILD_STATUS_LABEL: Record<BuildStatus | string, string> = {
  active: "in progress",
  parked: "paused",
  done: "done",
  failed: "failed",
  cancelled: "cancelled",
  superseded: "superseded by a newer build",
};

export function buildStatusLabel(status: string): string {
  return BUILD_STATUS_LABEL[status] ?? status;
}

/** A handful of park reasons this client knows plain English for; anything else is shown as "Paused
 *  — <reason>" so the server's own word is still on screen (never silently dropped, per the open-enum
 *  rule), just not pretending to be translated. */
const PARK_REASON_LABEL: Record<string, string> = {
  budget_exhausted: "Paused — ran out of budget",
  needs_input: "Paused — waiting for your input",
  manual_prerequisite: "Paused — needs something set up in the org first",
  repeated_failures: "Paused — too many attempts failed",
};

export function parkReasonLabel(reason: string | null | undefined): string {
  if (!reason) return "Paused";
  return PARK_REASON_LABEL[reason] ?? `Paused — ${reason}`;
}

const ERROR_CODE_LABEL: Record<string, string> = {
  unsupported_type: "type not supported — excluded",
};

/** The one-line reason a work item did not build, given its `status`/`error_code`/`depends_on`.
 *  `blocked` is rendered from `depends_on` (resolved to the blocking item's name when it is present
 *  in `itemsById`, else the raw id) rather than from `error_code`, because that is the field the
 *  server actually populates for a blocked item. */
export function workItemFailureReason(
  item: Pick<WorkItemView, "status" | "error_code" | "depends_on">,
  itemsById?: ReadonlyMap<string, Pick<WorkItemView, "full_name">>,
): string | null {
  if (item.status === "blocked") {
    const deps = item.depends_on ?? [];
    if (deps.length === 0) return "blocked";
    const names = deps.map((id) => itemsById?.get(id)?.full_name ?? id);
    return `blocked behind ${names.join(", ")}`;
  }
  if (item.error_code) {
    return ERROR_CODE_LABEL[item.error_code] ?? `failed — ${item.error_code}`;
  }
  if (item.status === "dropped") return "dropped";
  if (item.status === "failed") return "failed";
  return null;
}

/** `WorkItemView[]` indexed by `work_item_id`, for resolving a `depends_on` id to a name. */
export function indexWorkItems(items: readonly WorkItemView[]): Map<string, WorkItemView> {
  return new Map(items.map((i) => [i.work_item_id, i]));
}

// ── matching a package member to a work item (for the "View" button) ──────────────────────────────

/** `metadata_type::full_name`, case-folded — the join key between a `PackageMemberView` and the
 *  `WorkItemView` that produced it. Both carry `metadata_type`/`full_name` but no shared id, so this
 *  is the only way to line them up. */
export function itemKey(metadataType: string, fullName: string): string {
  return `${metadataType.toLowerCase()}::${fullName.toLowerCase()}`;
}

export function buildItemsByKey(items: readonly WorkItemView[]): Map<string, WorkItemView> {
  const map = new Map<string, WorkItemView>();
  for (const item of items) map.set(itemKey(item.metadata_type, item.full_name), item);
  return map;
}

/** The work item that produced this package member, when the build carries one with a body. */
export function matchMemberToWorkItem(
  member: Pick<PackageMemberView, "metadata_type" | "full_name">,
  itemsByKey: ReadonlyMap<string, WorkItemView>,
): WorkItemView | null {
  return itemsByKey.get(itemKey(member.metadata_type, member.full_name)) ?? null;
}

// ── org findings (E1 advisory, PlanView.org_findings) ───────────────────────────────────────────────

/** Groups org findings by `kind`, first-seen order (mirrors `groupExcludedByReason`'s shape) — `kind`
 *  is the only field this client can rely on; everything else in `OrgFindingView` is opaque JSON from
 *  `orgmodel_api.ComponentFinding.as_dict()`, with no pinned key set. */
export function groupFindingsByKind(findings: readonly OrgFindingView[]): OpGroup<OrgFindingView>[] {
  const buckets = new Map<string, OrgFindingView[]>();
  for (const f of findings) {
    const list = buckets.get(f.kind);
    if (list) list.push(f);
    else buckets.set(f.kind, [f]);
  }
  return Array.from(buckets, ([op, items]) => ({ op, items }));
}

export function orgFindingKindLabel(kind: string): string {
  return kind.replace(/_/g, " ");
}

/** A readable label for an E1 finding's `reason` code. `orgmodel_api.ComponentFinding` owns its own
 *  reason vocabulary (this client could not confirm a fixed list of it against the backend source),
 *  so this humanises the code (underscores to spaces) rather than pinning specific strings it cannot
 *  verify, per the same open-enum rule every other labeller in this module follows, never a raw
 *  token verbatim. */
export function orgFindingReasonLabel(reason: string): string {
  return reason.replace(/_/g, " ");
}

/** A one-line summary of a finding: `ComponentFinding.as_dict()` always carries `kind`, `reason`,
 *  `item_id`, `metadata_type` and `full_name` (confirmed against the peer, not guessed) — so this
 *  leads with the component the finding is about (`metadata_type full_name`), then a readable label
 *  for `reason`, joined by a colon; either half alone (a finding with no resolved component, or a
 *  reason this client cannot even humanise) still renders something rather than nothing. */
export function orgFindingSummary(f: OrgFindingView): string {
  const metadataType = typeof f.metadata_type === "string" ? f.metadata_type : null;
  const fullName = typeof f.full_name === "string" ? f.full_name : null;
  const component = [metadataType, fullName].filter(Boolean).join(" ") || null;
  const reason = typeof f.reason === "string" && f.reason ? orgFindingReasonLabel(f.reason) : null;
  if (component && reason) return `${component}: ${reason}`;
  return component ?? reason ?? f.kind;
}

export function groupExcludedByReason(excluded: readonly ExcludedComponentView[]): OpGroup<ExcludedComponentView>[] {
  const buckets = new Map<string, ExcludedComponentView[]>();
  for (const e of excluded) {
    const list = buckets.get(e.reason);
    if (list) list.push(e);
    else buckets.set(e.reason, [e]);
  }
  return Array.from(buckets, ([op, items]) => ({ op, items }));
}

// ── collapsing large groups ──────────────────────────────────────────────────────────────────────

/** Default number of rows shown before a group collapses to a count + disclosure. Applies to both
 *  plan-item groups (must scale to 5000 items) and build-item groups (60+ items). */
export const DEFAULT_VISIBLE_ROWS = 50;

/** Splits a group's items into what renders by default and what is behind "show more". Pure so the
 *  5000-item test can assert the visible slice is small without rendering anything. */
export function visibleSlice<T>(items: readonly T[], limit: number = DEFAULT_VISIBLE_ROWS): { visible: T[]; hiddenCount: number } {
  if (items.length <= limit) return { visible: [...items], hiddenCount: 0 };
  return { visible: items.slice(0, limit), hiddenCount: items.length - limit };
}

// ── save to branch (leg v2-save-branch) ─────────────────────────────────────────────────────────────

/** `SaveBranchIn.branch` shape: `^[A-Za-z0-9._/-]{1,200}$`, applied to the TRIMMED value (the server
 *  strips whitespace before validating too — an all-space input 422s as empty, not as a shape miss). */
const BRANCH_NAME_PATTERN = /^[A-Za-z0-9._/-]{1,200}$/;

export function isValidBranchName(name: string): boolean {
  return BRANCH_NAME_PATTERN.test(name.trim());
}

/** `"{metadata_type}:{full_name}"` — the `component_keys` wire shape §3.16 matches case-insensitively
 *  against the package's own members (a single colon; NOT `itemKey`'s double-colon plan/build join key). */
export function packageMemberComponentKey(m: Pick<PackageMemberView, "metadata_type" | "full_name">): string {
  return `${m.metadata_type}:${m.full_name}`;
}

/** Mirrors MSA's deploying-only default selection when `component_keys` is omitted (§3.16 "Member and
 *  file selection", OD-2): every member with bytes to send that is not a `delete`-op member and that
 *  deploys (`deploys !== false`; a package from a server that omits the key is treated as deploying).
 *  Failed and held members stay in the checklist, unticked, so the user can opt in by ticking them. */
export function defaultBranchSaveMembers(members: readonly PackageMemberView[]): string[] {
  return members
    .filter((m) => !!m.body_sha256 && m.op !== "delete" && m.deploys !== false)
    .map(packageMemberComponentKey);
}

type SaveMemberFacts = {
  deploys?: boolean | null;
  held_reason?: string | null;
  validation_status?: string | null;
  body_changed?: boolean | null;
};

/** True for a non-deploying member that is unchanged from the org baseline (nothing to save). */
function isUnchangedMember(m: SaveMemberFacts): boolean {
  return m.deploys === false && m.body_changed === false;
}

/** True for a non-deploying member that is held back or failed validation (the "opt in by ticking" rows). */
export function isFailedOrHeldMember(m: SaveMemberFacts): boolean {
  return m.deploys === false && !isUnchangedMember(m) && (!!m.held_reason || m.validation_status === "validation_failed");
}

/** Why a member is not ticked by default, for the checklist row; null when it deploys. Same order as the
 *  server: unchanged, then held, then failed. `held_reason` is an open string: only the two
 *  `dependency_failed_*` values are understood, anything else is "held". */
export function saveNotTickedReason(m: SaveMemberFacts): string | null {
  if (m.deploys !== false) return null;
  if (m.body_changed === false) return "unchanged from org: nothing to save";
  if (m.held_reason) {
    if (m.held_reason === "dependency_failed_create") return "held: depends on a create that does not deploy";
    if (m.held_reason === "dependency_failed_modify") return "held: depends on a change that does not deploy";
    return "held: reason not understood";
  }
  return m.validation_status === "validation_failed" ? "validation failed" : "does not deploy: build did not finish";
}

/**
 * A skipped file's `status` is the skip reason, not a status word (§3.16). Fixed copy for the reasons this client knows;
 * anything else gets the neutral fallback, never the server's own words.
 */
const SAVE_SKIP_REASON_LABEL: Record<string, string> = {
  unchanged: "unchanged from the org, so there was nothing to save",
  full_merge_unavailable: "the branch has a newer version of this file and it cannot be merged automatically",
  body_cap: "the batch was over the size limit for one request",
  batch_not_sent: "Not sent — the save stopped before this part was sent",
  meta_read_only: "this metadata's sidecar file cannot be written on its own",
  base_unknown: "no known baseline to compare against. Skipped rather than risk overwriting a concurrent edit",
};

/**
 * `detail` is the peer's one-line note; only its leading machine token is read (`token` or `token:<code>`), the rest is
 * never shown. A fixed spec vocabulary (rule 22 role iii), not prose matching.
 */
const SAVE_DETAIL_TOKEN_LABEL: Record<string, string> = {
  body_cap_alone: "this file alone is over the size limit",
  body_cap: "the batch was over the size limit",
  peer_unavailable: "the branch service was unavailable",
  replay_refused: "the branch service refused a repeated request",
  peer_rejected: "the branch service rejected it",
  branch_conflict: "the branch changed underneath it",
};

/** The leading machine token of a `detail`, and its `:<code>` part when it is a plain machine code. */
export function saveDetailToken(detail: string | null | undefined): { token: string; code: string | null } | null {
  if (typeof detail !== "string") return null;
  const m = /^([a-z][a-z_]{0,40})(?::([A-Za-z0-9_.-]{1,64}))?(?![A-Za-z0-9_])/.exec(detail.trim());
  return m ? { token: m[1], code: m[2] ?? null } : null;
}

export const SAVE_SKIP_FALLBACK = "not saved (reason not recognised)";
export const SAVE_HAS_CONFLICTS_NOTE = "contains conflict markers; resolve them in the branch";

/** Words for one skipped file: the `status` reason, refined by the `detail` token when both are known. */
export function saveSkipReasonLabel(status: string, detail?: string | null): string {
  const base = SAVE_SKIP_REASON_LABEL[status] ?? SAVE_SKIP_FALLBACK;
  const d = saveDetailToken(detail);
  const why = d ? SAVE_DETAIL_TOKEN_LABEL[d.token] : undefined;
  if (!why) return base;
  const code = d?.token === "peer_rejected" && d.code ? ` (${d.code})` : "";
  // body_cap_alone (detail) refines a body_cap status; a detail that restates the status adds nothing.
  return why === base ? base : `${base} (${why}${code})`;
}

export function isSkippedSaveFile(f: Pick<SaveBranchFileResult, "result">): boolean {
  return f.result === "skipped";
}

/** `at` (an ISO timestamp) as the reader's own local time, not the raw wire string — `branch_saves`
 *  history and any other save-branch timestamp render through this rather than the bare ISO. Falls
 *  back to the raw string for anything that does not parse, never "Invalid Date". */
export function formatLocalTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** One row of `BranchConflictBody.files` — the PEER's own conflicting-files list, passed through
 *  verbatim (READ_MODEL_SPEC.md 3.16) with no pinned shape of its own (unlike the legacy lane's
 *  `SaveConflict.files`, which IS pinned to `{file_id, reason}`). Best-effort field extraction from
 *  whichever of the peer's own plausible keys are present, rather than a raw JSON dump — this is a
 *  table a reader scans, not a debug pane. */
export interface BranchConflictFileRow {
  component: string;
  path: string;
  status: string;
  reason: string;
}

function firstStringField(obj: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

const NOT_GIVEN = "not given";

export function branchConflictFileRow(f: unknown): BranchConflictFileRow {
  const o = f && typeof f === "object" ? (f as Record<string, unknown>) : {};
  return {
    component: firstStringField(o, ["component_key", "component", "metadata_type"]) ?? NOT_GIVEN,
    path: firstStringField(o, ["path", "file_name", "file_id"]) ?? NOT_GIVEN,
    status: firstStringField(o, ["status"]) ?? NOT_GIVEN,
    reason: firstStringField(o, ["reason", "message", "detail"]) ?? NOT_GIVEN,
  };
}

// ── line diff (ItemDiffView) ─────────────────────────────────────────────────────────────────────

export type DiffLineType = "equal" | "add" | "remove";

export interface DiffLine {
  type: DiffLineType;
  text: string;
}

/** Above this many (lines-in-a × lines-in-b) cells, the LCS table gets skipped in favour of a plain
 *  "everything changed" result — bounds the worst case for a body near the 512 KiB item-body cap
 *  (`types.ts` R4) instead of hanging the tab on a huge generated file. */
const DIFF_CELL_BUDGET = 4_000_000;

/**
 * `diffLines`' own split prep: CRLF → LF and ONE final `\n` dropped, so a trailing newline never
 * shows as a spurious empty last line. NOT the "is it the same file" rule: MSA owns that
 * (`baselines.normalize_for_compare`); the status comparison is `normalizeForStatus` below.
 */
export function normalizeBodyForCompare(text: string): string {
  const lf = text.replace(/\r\n/g, "\n");
  return lf.endsWith("\n") ? lf.slice(0, -1) : lf;
}

/**
 * The GUI fallback for the server's `normalize_for_compare` (`replace("\r\n","\n").rstrip()`): CRLF →
 * LF, trailing whitespace dropped. JS `trimEnd` and Python `rstrip` differ only on exotic whitespace;
 * the server is the owner and its wire verdict (`unchanged` / `body_changed`) wins when present.
 */
export function normalizeForStatus(text: string): string {
  return text.replace(/\r\n/g, "\n").trimEnd();
}

/**
 * A unified line diff between two texts, via a standard LCS table (patience-diff-quality output is
 * not the goal — a plain, honest before/after for a reviewer is). Pure and dependency-free: the repo
 * has no diff library (`FileDiff.tsx`'s hunk diff comes pre-computed from the server over a REST call
 * this read-only surface has no equivalent of), so this is the smallest correct implementation rather
 * than a new dependency for one view.
 */
export function diffLines(baseline: string, modified: string): DiffLine[] {
  const na = normalizeBodyForCompare(baseline);
  const nb = normalizeBodyForCompare(modified);
  const a = na.length ? na.split("\n") : [];
  const b = nb.length ? nb.split("\n") : [];
  const n = a.length;
  const m = b.length;

  if (n * m > DIFF_CELL_BUDGET) {
    return [...a.map((text): DiffLine => ({ type: "remove", text })), ...b.map((text): DiffLine => ({ type: "add", text }))];
  }

  // dp[i][j] = length of the LCS of a[i:] and b[j:].
  const dp: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const result: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      result.push({ type: "equal", text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      result.push({ type: "remove", text: a[i] });
      i++;
    } else {
      result.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) {
    result.push({ type: "remove", text: a[i] });
    i++;
  }
  while (j < m) {
    result.push({ type: "add", text: b[j] });
    j++;
  }
  return result;
}

// ── validation history (contracts 0.2.30 `ValidationView.checks`) ─────────────────────────────────

export type CheckVerdict = "passed" | "failed" | "unknown";

/** `ok` is `null` when the org never reported a result: that is "unknown", never "failed". */
export function checkVerdict(check: Pick<CheckView, "ok">): CheckVerdict {
  return check.ok === null || check.ok === undefined ? "unknown" : check.ok ? "passed" : "failed";
}

export const CHECK_VERDICT_LABEL: Record<CheckVerdict, string> = {
  passed: "Passed",
  failed: "Failed",
  unknown: "No result yet",
};

/** What started a check (open enum: an unknown origin renders as "Checked", never as its raw code). */
export function checkOriginLabel(origin: string | null | undefined): string {
  if (origin === "sweep") return "Checked after the build";
  if (origin === "revalidate") return "You re-ran it";
  return "Checked";
}

/** Newest first by `created_at` (else `finished_at`); a row with no time keeps its place after the dated ones. */
export function checksNewestFirst(checks: readonly CheckView[] | undefined): CheckView[] {
  const when = (c: CheckView): number => {
    const t = Date.parse(c.created_at ?? c.finished_at ?? "");
    return Number.isNaN(t) ? -Infinity : t;
  };
  return (checks ?? []).map((c, i) => ({ c, i })).sort((a, b) => when(b.c) - when(a.c) || a.i - b.i).map((x) => x.c);
}

const CHECK_SUMMARY_MAX = 160;

/**
 * One line saying why a check failed: the org's own `detail` if it gave one, else the first failing component with its first
 * message and a count of the rest. Empty for a check that did not fail. Bounded so a long org message cannot blow up a row.
 */
export function checkErrorSummary(check: CheckView): string {
  if (check.ok !== false) return "";
  const clip = (t: string) => (t.length > CHECK_SUMMARY_MAX ? `${t.slice(0, CHECK_SUMMARY_MAX - 1)}…` : t);
  if (check.detail) return clip(check.detail);
  const entries = Object.entries(check.failures ?? {});
  if (entries.length === 0) return "";
  const [component, messages] = entries[0];
  const first = Array.isArray(messages) ? (messages[0] ?? "") : messages;
  const head = first ? `${component}: ${first}` : component;
  return clip(entries.length > 1 ? `${head} (and ${entries.length - 1} more)` : head);
}

/** The org's deploy test level in words (Salesforce `testLevel`); an unknown value is shown as received, never dropped. */
const TEST_LEVEL_WORDS: Record<string, string> = {
  NoTestRun: "No tests run",
  RunSpecifiedTests: "Specified tests only",
  RunLocalTests: "Your org's own tests",
  RunAllTestsInOrg: "Every test in the org",
};

export function testLevelWords(level: string): string {
  return TEST_LEVEL_WORDS[level] ?? level;
}

/** A clock time ("2:02 PM") for an ISO timestamp; null when it does not parse. */
export function formatClock(iso: string): string | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
